import { type BlockConfig, linkField } from './_types';

/**
 * Payload `Block` config sidecar for the `richTextSection` block.
 *
 * Decomposed from `src/pages/legal/LegalPageLayout.tsx` plus blog detail
 * + breach article + `/geo-blocked` + `/delete-my-account`. At cutover
 * the `body` field becomes a Payload Lexical richText field and the
 * marketing-app's Lexical renderer slots in. Today the Vite-era page
 * passes either `body` (ReactNode) or `bodyHtml` (string) directly to
 * the React component; the sidecar declares the cutover-shape `body:
 * richText` field as the canonical authoring surface.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `rich-text-section.tsx` / `rich-text-section.payload.ts`
 *   React component export:  `RichTextSection`
 *   Payload `slug`:          `richTextSection`
 *   Payload `interfaceName`: `RichTextSectionBlock`
 */

export const RichTextSectionBlock: BlockConfig = {
  slug: 'richTextSection',
  interfaceName: 'RichTextSectionBlock',
  labels: {
    singular: 'Rich Text Section',
    plural: 'Rich Text Section Blocks',
  },
  fields: [
    {
      name: 'title',
      type: 'text',
      label: 'Title',
    },
    {
      name: 'lastUpdated',
      type: 'text',
      label: 'Last updated',
      admin: {
        description:
          'Free-form date string rendered below the title (e.g. "January 23, 2026").',
      },
    },
    {
      name: 'body',
      type: 'richText',
      label: 'Body',
      admin: {
        description:
          'Lexical richText body. At cutover this drives the rendered prose. In reklaim-app v1.x, pages pass either inline JSX children or a pre-rendered HTML string into the React component; this field becomes authoritative once the block lives inside Payload.',
      },
    },
    {
      name: 'tableOfContents',
      type: 'array',
      label: 'Table of contents',
      labels: { singular: 'Item', plural: 'Items' },
      maxRows: 30,
      admin: {
        description:
          'Anchor links rendered in a sidebar nav. Each `id` should match a `section[id]` inside the body.',
      },
      fields: [
        { name: 'id', type: 'text', required: true, label: 'Anchor id' },
        { name: 'title', type: 'text', required: true, label: 'Title' },
      ],
    },
    {
      name: 'contactFooter',
      type: 'group',
      label: 'Contact footer',
      fields: [
        { name: 'heading', type: 'text', label: 'Heading' },
        {
          name: 'body',
          type: 'textarea',
          label: 'Body',
          admin: { rows: 2 },
        },
        { name: 'email', type: 'text', label: 'Email address' },
        { name: 'address', type: 'text', label: 'Postal address' },
      ],
    },
    {
      name: 'relatedLinks',
      type: 'array',
      label: 'Related links',
      labels: { singular: 'Link', plural: 'Links' },
      maxRows: 12,
      fields: [
        linkField({ name: 'href', label: 'URL', required: true }),
        { name: 'label', type: 'text', required: true, label: 'Label' },
        {
          name: 'external',
          type: 'checkbox',
          defaultValue: false,
          label: 'External link?',
        },
      ],
    },
  ],
};

export default RichTextSectionBlock;
